import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';
import http from '@/services/http';
import type { ApiResponse } from '@/types/types';

export interface FilterOption {
  id: number;
  name?: string;
  display_string?: string;
}

export interface UseFilterOptionsParams {
  slug: string;
  searchQuery?: Ref<string> | string;
  enabled?: Ref<boolean> | boolean;
}

/**
 * Fetches filter options from the API for a given model slug.
 * Used for populating combobox options in filter builders.
 *
 * @param slug - The model slug (e.g., 'genre', 'tag', 'location_zone')
 * @param searchQuery - Optional search query to filter results
 * @param enabled - Whether the query should be enabled
 * @returns Query result with options array
 */
/**
 * Shared fetch for entity filter options: tries POST /api/{slug}/search (searchable
 * models, capped at 1000 options), falling back to the index endpoint. Used by this
 * composable and by the FilterBuilder's useFilterOptions slug handlers.
 */
export async function fetchEntityFilterOptions(slug: string): Promise<FilterOption[]> {
  // For now, fetch all options and filter client-side for better UX
  // We can add server-side filtering later if needed for large datasets
  try {
    const response = await http.post(`/api/${slug}/search`, {
      perPage: 1000, // Fetch a large number of options (first cursor page; no count needed)
      withTotal: false,
    });

    const data = response.data as ApiResponse;
    return (data.data || []).map((item: any): FilterOption => ({
      id: item.id,
      name: item.name,
      display_string: item.display_string || item.name
    }));
  } catch {
    // If search endpoint fails, try index endpoint
    try {
      const response = await http.get(`/api/${slug}`);
      const data = response.data as any;
      const items = Array.isArray(data) ? data : (data.data || []);
      return items.map((item: any): FilterOption => ({
        id: item.id,
        name: item.name,
        display_string: item.display_string || item.name
      }));
    } catch (indexError) {
      // If both fail, return empty array
      console.warn(`Failed to fetch filter options for slug: ${slug}`, indexError);
      return [];
    }
  }
}

export function useFilterOptions({ slug, searchQuery, enabled = true }: UseFilterOptionsParams) {
  // Note: searchQuery is for future server-side filtering, currently filtering is client-side

  void searchQuery;
  const queryKey = ['filterOptions', slug];
  const queryFn = () => fetchEntityFilterOptions(slug);

  const isEnabled = computed(() => {
    if (typeof enabled === 'boolean') {
      return enabled && Boolean(slug);
    }
    return enabled.value && Boolean(slug);
  });

  return useQuery({
    queryKey,
    queryFn,
    retry: 0,
    enabled: isEnabled
  });
}

